// src/newDashboard/shared/ScanCalendarButton.jsx
//
// "Scan Calendar Photo" button that opens a modal: pick a bar, take/choose a
// photo of a calendar, get it read by AI into draft EVENTS and SPECIALS,
// review/edit/reclassify each one, then save the ones you keep — events go
// to the `events` collection, specials get appended to the bar's own
// `specials` list, matching how each is actually used elsewhere in the app.
//
// IMPORTANT: nothing saves automatically. Every draft requires a human to
// look at it and hit Save — dollar jackpot amounts and gambling-license
// content are too high-stakes to auto-publish from a photo scan.

import React, { useState } from "react";
import { collection, addDoc, doc, updateDoc, arrayUnion, Timestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { extractEventsFromCalendarImage } from "../../utils/firebaseFunctions";

const DAYS_FULL = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const EVENT_CATEGORIES = ["Gambling", "Entertainment", "Sports", "Other"];
const SPECIAL_CATEGORIES = ["Daily Food", "Daily Drink", "Happy Hour", "Late Night", "Birthday"];

// Resizes the photo down before sending it, to keep uploads small and fast.
function compressImageToBase64(file, maxWidth = 1200, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxWidth / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL("image/jpeg", quality);
        resolve({ base64: dataUrl.split(",")[1], mediaType: "image/jpeg" });
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Turns "6:00pm" / "6pm" / "6:00 PM" into "18:00" for time inputs.
function parseTimeTo24h(rawTime) {
  if (!rawTime) return "";
  const match = String(rawTime).trim().match(/^(\d{1,2}):?(\d{2})?\s*([AaPp][Mm])?$/);
  if (!match) return "";
  let hour = parseInt(match[1], 10);
  const minute = match[2] || "00";
  const period = match[3] ? match[3].toLowerCase() : null;
  if (period === "pm" && hour !== 12) hour += 12;
  if (period === "am" && hour === 12) hour = 0;
  return `${String(hour).padStart(2, "0")}:${minute}`;
}

export default function ScanCalendarButton({ myBars, user, role, onEventsSaved }) {
  const [isOpen, setIsOpen] = useState(false);
  const [barId, setBarId] = useState(myBars[0]?.id || "");
  const [isScanning, setIsScanning] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [drafts, setDrafts] = useState(null);

  const reset = () => {
    setDrafts(null);
    setError("");
    setIsScanning(false);
    setIsSaving(false);
  };

  const close = () => {
    setIsOpen(false);
    reset();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !barId) return;
    setError("");
    setIsScanning(true);
    try {
      const { base64, mediaType } = await compressImageToBase64(file);
      const result = await extractEventsFromCalendarImage({
        imageBase64: base64,
        mediaType,
        barId,
      });

      const withEditableFields = result.draftEvents.map((item, i) => {
        const itemType = item.itemType === "special" ? "special" : "event";
        return {
          ...item,
          _id: i,
          _include: true,
          _itemType: itemType,
          // event fields
          _category: item.category || "Gambling",
          _eventRecurrence: item.recurrence === "one-time" ? "one-time" : "weekly",
          _dayOfWeek: item.dayOfWeek || DAYS_FULL[1],
          _time24: parseTimeTo24h(item.time),
          // special fields
          _specialCategory: item.specialCategory || "Daily Food",
          _specialRecurrence:
            item.recurrence === "days" || item.recurrence === "one-time" ? item.recurrence : "daily",
          _days: item.days || [],
          _startTime24: parseTimeTo24h(item.startTime),
          _endTime24: parseTimeTo24h(item.endTime),
          // shared
          _oneTimeDate: "",
        };
      });
      setDrafts(withEditableFields);
    } catch (err) {
      console.error(err);
      setError(
        "Couldn't read that photo. Try a clearer picture with better lighting, and make sure the whole calendar is visible."
      );
    } finally {
      setIsScanning(false);
      e.target.value = "";
    }
  };

  const updateDraft = (id, field, value) => {
    setDrafts((prev) => prev.map((d) => (d._id === id ? { ...d, [field]: value } : d)));
  };

  const toggleDraftDay = (id, dayShort) => {
    setDrafts((prev) =>
      prev.map((d) => {
        if (d._id !== id) return d;
        const has = d._days.includes(dayShort);
        return { ...d, _days: has ? d._days.filter((x) => x !== dayShort) : [...d._days, dayShort] };
      })
    );
  };

  const handleSave = async () => {
    const toSave = drafts.filter((d) => d._include);

    const missingDate = toSave.some((d) => {
      const rec = d._itemType === "event" ? d._eventRecurrence : d._specialRecurrence;
      return rec === "one-time" && !d._oneTimeDate;
    });
    if (missingDate) {
      setError("Every one-time item needs a date filled in before saving.");
      return;
    }

    setIsSaving(true);
    setError("");
    try {
      for (const d of toSave) {
        if (d._itemType === "event") {
          await addDoc(collection(db, "events"), {
            barId,
            title: d.title,
            category: d._category,
            recurrence: d._eventRecurrence,
            dayOfWeek: d._eventRecurrence === "weekly" ? d._dayOfWeek : "",
            date: d._eventRecurrence === "one-time" ? d._oneTimeDate : "",
            time: d._time24,
            details: d.details || "",
            createdBy: user.uid,
            createdByRole: role,
            createdAt: Timestamp.now(),
          });
        } else {
          const specialObj = {
            id: `${Date.now()}-${d._id}`,
            category: d._specialCategory,
            title: d.title,
            details: d.details || "",
            recurrence: d._specialRecurrence,
            days: d._specialRecurrence === "days" ? d._days : [],
            date: d._specialRecurrence === "one-time" ? d._oneTimeDate : "",
            startTime: d._startTime24 || "",
            endTime: d._endTime24 || "",
          };
          await updateDoc(doc(db, "bars", barId), {
            specials: arrayUnion(specialObj),
          });
        }
      }
      close();
      onEventsSaved();
    } catch (err) {
      console.error(err);
      setError("Something went wrong saving these. Nothing was lost — try again.");
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        style={{
          background: "transparent",
          border: "1px solid rgba(255,255,255,0.2)",
          borderRadius: "8px",
          color: "#fff",
          padding: "10px 16px",
          cursor: "pointer",
          marginBottom: "20px",
        }}
      >
        Scan Calendar Photo
      </button>
    );
  }

  const includedCount = drafts ? drafts.filter((d) => d._include).length : 0;
  const chipStyle = (active) => ({
    padding: "6px 12px",
    borderRadius: "20px",
    border: active ? "2px solid #A855F7" : "1px solid rgba(255,255,255,0.2)",
    background: active ? "rgba(168,85,247,0.15)" : "transparent",
    color: "#fff",
    cursor: "pointer",
    fontSize: "15px",
  });

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "20px",
      }}
    >
      <div
        style={{
          background: "#0D0B1A",
          borderRadius: "12px",
          padding: "24px",
          maxWidth: "600px",
          width: "100%",
          maxHeight: "85vh",
          overflowY: "auto",
          color: "#fff",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <h3 style={{ margin: 0 }}>Scan Calendar Photo</h3>
          <button
            onClick={close}
            style={{ background: "transparent", border: "none", color: "#fff", fontSize: "20px", cursor: "pointer" }}
          >
            ×
          </button>
        </div>

        {!drafts && !isScanning && (
          <>
            <label style={{ display: "block", marginBottom: "8px" }}>Which bar is this calendar for?</label>
            <select
              value={barId}
              onChange={(e) => setBarId(e.target.value)}
              style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "none", marginBottom: "16px" }}
            >
              {myBars.map((bar) => (
                <option key={bar.id} value={bar.id}>
                  {bar.name || "Unnamed Bar"}
                </option>
              ))}
            </select>

            <label
              style={{
                display: "block",
                textAlign: "center",
                padding: "30px",
                border: "2px dashed rgba(255,255,255,0.2)",
                borderRadius: "10px",
                cursor: "pointer",
              }}
            >
              Tap to take or choose a photo of the calendar
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
            </label>
          </>
        )}

        {isScanning && <p>Reading the photo, one moment…</p>}

        {error && <p style={{ color: "#F472B6", marginTop: "12px" }}>{error}</p>}

        {drafts && (
          <>
            <p style={{ color: "#B8B0C8", fontSize: "16px", marginBottom: "16px" }}>
              Found {drafts.length} item{drafts.length === 1 ? "" : "s"}. Each one is marked Event or Special —
              switch it if that's wrong, review the details, uncheck any you don't want, then save.
            </p>
            {drafts.map((d) => (
              <div
                key={d._id}
                style={{
                  background: "#151225",
                  border: d.needsReview ? "1px solid #F59E0B" : "1px solid rgba(255,255,255,0.08)",
                  borderRadius: "10px",
                  padding: "14px",
                  marginBottom: "12px",
                }}
              >
                <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                  <input
                    type="checkbox"
                    checked={d._include}
                    onChange={(e) => updateDraft(d._id, "_include", e.target.checked)}
                    style={{ marginTop: "6px" }}
                  />
                  <div style={{ flex: 1 }}>
                    {d.needsReview && (
                      <p style={{ color: "#F59E0B", fontSize: "15px", margin: "0 0 8px" }}>
                        ⚠ Double check this one{d.reviewNote ? `: ${d.reviewNote}` : ""}
                      </p>
                    )}

                    <div style={{ display: "flex", gap: "8px", marginBottom: "10px" }}>
                      <button
                        type="button"
                        style={chipStyle(d._itemType === "event")}
                        onClick={() => updateDraft(d._id, "_itemType", "event")}
                      >
                        Event
                      </button>
                      <button
                        type="button"
                        style={chipStyle(d._itemType === "special")}
                        onClick={() => updateDraft(d._id, "_itemType", "special")}
                      >
                        Special
                      </button>
                    </div>

                    <input
                      type="text"
                      value={d.title}
                      onChange={(e) => updateDraft(d._id, "title", e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px",
                        borderRadius: "6px",
                        border: "none",
                        marginBottom: "8px",
                        fontWeight: 600,
                      }}
                    />

                    {d._itemType === "event" ? (
                      <div style={{ display: "flex", gap: "8px", marginBottom: "8px", flexWrap: "wrap" }}>
                        <select
                          value={d._category}
                          onChange={(e) => updateDraft(d._id, "_category", e.target.value)}
                          style={{ padding: "6px", borderRadius: "6px", border: "none" }}
                        >
                          {EVENT_CATEGORIES.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                        <select
                          value={d._eventRecurrence}
                          onChange={(e) => updateDraft(d._id, "_eventRecurrence", e.target.value)}
                          style={{ padding: "6px", borderRadius: "6px", border: "none" }}
                        >
                          <option value="daily">Every day</option>
                          <option value="weekly">Weekly</option>
                          <option value="one-time">One-time</option>
                        </select>
                        {d._eventRecurrence === "weekly" && (
                          <select
                            value={d._dayOfWeek}
                            onChange={(e) => updateDraft(d._id, "_dayOfWeek", e.target.value)}
                            style={{ padding: "6px", borderRadius: "6px", border: "none" }}
                          >
                            {DAYS_FULL.map((day) => (
                              <option key={day} value={day}>
                                {day}
                              </option>
                            ))}
                          </select>
                        )}
                        {d._eventRecurrence === "one-time" && (
                          <input
                            type="date"
                            value={d._oneTimeDate}
                            onChange={(e) => updateDraft(d._id, "_oneTimeDate", e.target.value)}
                            style={{ padding: "6px", borderRadius: "6px", border: "none" }}
                          />
                        )}
                        <input
                          type="time"
                          value={d._time24}
                          onChange={(e) => updateDraft(d._id, "_time24", e.target.value)}
                          style={{ padding: "6px", borderRadius: "6px", border: "none" }}
                        />
                      </div>
                    ) : (
                      <div style={{ marginBottom: "8px" }}>
                        <div style={{ display: "flex", gap: "8px", marginBottom: "8px", flexWrap: "wrap" }}>
                          <select
                            value={d._specialCategory}
                            onChange={(e) => updateDraft(d._id, "_specialCategory", e.target.value)}
                            style={{ padding: "6px", borderRadius: "6px", border: "none" }}
                          >
                            {SPECIAL_CATEGORIES.map((c) => (
                              <option key={c} value={c}>
                                {c}
                              </option>
                            ))}
                          </select>
                          <select
                            value={d._specialRecurrence}
                            onChange={(e) => updateDraft(d._id, "_specialRecurrence", e.target.value)}
                            style={{ padding: "6px", borderRadius: "6px", border: "none" }}
                          >
                            <option value="daily">Every day</option>
                            <option value="days">Certain days</option>
                            <option value="one-time">One-time</option>
                          </select>
                          {d._specialRecurrence === "one-time" && (
                            <input
                              type="date"
                              value={d._oneTimeDate}
                              onChange={(e) => updateDraft(d._id, "_oneTimeDate", e.target.value)}
                              style={{ padding: "6px", borderRadius: "6px", border: "none" }}
                            />
                          )}
                        </div>

                        {d._specialRecurrence === "days" && (
                          <div style={{ display: "flex", gap: "4px", marginBottom: "8px", flexWrap: "wrap" }}>
                            {DAYS_SHORT.map((day) => (
                              <button
                                key={day}
                                type="button"
                                style={chipStyle(d._days.includes(day))}
                                onClick={() => toggleDraftDay(d._id, day)}
                              >
                                {day}
                              </button>
                            ))}
                          </div>
                        )}

                        <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                          <label style={{ fontSize: "15px", color: "#B8B0C8" }}>Start</label>
                          <input
                            type="time"
                            value={d._startTime24}
                            onChange={(e) => updateDraft(d._id, "_startTime24", e.target.value)}
                            style={{ padding: "6px", borderRadius: "6px", border: "none" }}
                          />
                          <label style={{ fontSize: "15px", color: "#B8B0C8" }}>End</label>
                          <input
                            type="time"
                            value={d._endTime24}
                            onChange={(e) => updateDraft(d._id, "_endTime24", e.target.value)}
                            style={{ padding: "6px", borderRadius: "6px", border: "none" }}
                          />
                          <span style={{ fontSize: "14px", color: "#8A8296" }}>(leave blank for "all day")</span>
                        </div>
                      </div>
                    )}

                    <textarea
                      value={d.details}
                      onChange={(e) => updateDraft(d._id, "details", e.target.value)}
                      style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "none", height: "50px" }}
                    />
                  </div>
                </div>
              </div>
            ))}

            <div style={{ display: "flex", gap: "10px", marginTop: "16px" }}>
              <button
                onClick={handleSave}
                disabled={isSaving}
                style={{
                  background: "linear-gradient(90deg, #6D28D9, #A855F7)",
                  border: "none",
                  padding: "12px",
                  borderRadius: "8px",
                  color: "#fff",
                  fontWeight: 600,
                  cursor: "pointer",
                  flex: 1,
                }}
              >
                {isSaving ? "Saving…" : `Save ${includedCount} Item${includedCount === 1 ? "" : "s"}`}
              </button>
              <button
                onClick={reset}
                style={{
                  background: "transparent",
                  border: "1px solid rgba(255,255,255,0.2)",
                  padding: "12px 20px",
                  borderRadius: "8px",
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                Scan Different Photo
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
